/**
 * ReportsPanel — Phase 4 gap-fill shipping economics view. Aggregated
 * from every label bought via Ship with Verqo (labels bought, postage
 * billed, insurance, avg delivery days, etc.).
 * Extracted from SellerDropship.jsx in Phase 4 refactor.
 */
export default function ReportsPanel({ reports, reportsDays, setReportsDays }) {
  return (
  <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="fulfillment-reports-panel">
    <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between gap-2">
      <div>
        <div className="text-[13px] font-medium">Shipping economics · last {reportsDays} days</div>
        <div className="text-[11px] text-foreground/55 mt-0.5">
          Aggregated from every label bought via Ship with Verqo. Refreshed on open.
        </div>
      </div>
      <div className="flex items-center gap-1">
        {[7, 30, 90].map(d => (
          <button key={d}
            onClick={() => setReportsDays(d)}
            data-testid={`reports-window-${d}d`}
            className={`text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border ${
              reportsDays === d
                ? "bg-foreground text-background border-foreground"
                : "border-border bg-white hover:bg-secondary"
            }`}>
            {d}d
          </button>
        ))}
      </div>
    </div>

    {!reports ? (
      <div className="p-10 text-center text-[12px] text-foreground/55">Loading reports…</div>
    ) : reports.labels_bought === 0 ? (
      <div className="p-10 text-center text-[12px] text-foreground/55">
        No labels bought in the last {reportsDays} days.
      </div>
    ) : (
      <div className="p-5 space-y-5">
        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Labels bought",       value: reports.labels_bought,                          suffix: "" },
            { label: "Postage billed",      value: `$${reports.postage_billed.toLocaleString()}`,   suffix: "" },
            { label: "Verqo markup",        value: `$${reports.verqo_markup_earned.toLocaleString()}`, suffix: `(${reports.postage_billed ? ((reports.verqo_markup_earned / reports.postage_billed) * 100).toFixed(1) : 0}%)`, accent: "emerald" },
            { label: "Avg cost / label",    value: `$${reports.avg_cost_per_label.toLocaleString()}`, suffix: "" },
            { label: "Insurance premium",   value: `$${reports.insurance_premium.toLocaleString()}`, suffix: "" },
            { label: "Postage spent",       value: `$${reports.postage_spent.toLocaleString()}`,    suffix: "Verqo Shipping billed" },
            { label: "Window",              value: `${reports.window_days}d`,                      suffix: "" },
          ].map(k => (
            <div key={k.label} className="rounded-xl border border-border p-3 bg-white">
              <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/55">{k.label}</div>
              <div className={`text-[20px] font-semibold mt-1 ${k.accent === "emerald" ? "text-emerald-700" : ""}`} data-testid={`kpi-${k.label.replace(/[^a-z]+/gi,"-").toLowerCase()}`}>
                {k.value}
              </div>
              {k.suffix && <div className="text-[10.5px] text-foreground/55 mt-0.5">{k.suffix}</div>}
            </div>
          ))}
        </div>

        {/* Two-up: destinations + carrier mix */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top destinations */}
          <div className="rounded-xl border border-border bg-white">
            <div className="px-4 py-2.5 border-b border-border text-[11px] mono uppercase tracking-[0.14em] text-foreground/60">
              Top destinations
            </div>
            {reports.top_destinations.length === 0 ? (
              <div className="p-4 text-[12px] text-foreground/55">No data yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {reports.top_destinations.map(d => (
                  <div key={d.state} className="px-4 py-2 flex items-center justify-between text-[12.5px]" data-testid={`dest-${d.state}`}>
                    <span className="mono">{d.state || "??"}</span>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full bg-foreground/70"
                          style={{ width: `${Math.min(100, (d.count / (reports.top_destinations[0].count || 1)) * 100)}%` }}/>
                      </div>
                      <span className="text-foreground/60 mono w-8 text-right">{d.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Carrier mix */}
          <div className="rounded-xl border border-border bg-white">
            <div className="px-4 py-2.5 border-b border-border text-[11px] mono uppercase tracking-[0.14em] text-foreground/60">
              Carrier mix
            </div>
            {reports.carrier_mix.length === 0 ? (
              <div className="p-4 text-[12px] text-foreground/55">No data yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {reports.carrier_mix.slice(0, 6).map(c => (
                  <div key={c.carrier} className="px-4 py-2 flex items-center justify-between text-[12.5px]" data-testid={`carrier-mix-${c.carrier}`}>
                    <span className="mono">{c.carrier}</span>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full bg-indigo-500"
                          style={{ width: `${Math.min(100, (c.count / (reports.carrier_mix[0].count || 1)) * 100)}%` }}/>
                      </div>
                      <span className="text-foreground/60 mono w-8 text-right">{c.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
  );
}
